import React, { createContext, useContext } from 'react'
import { useSubscription } from '@/hooks/useBilling'
import { SubscriptionView } from '@/api/billingApi'

interface BillingContextType {
    subscription: SubscriptionView | null
    isLoading: boolean
    isFeatureEnabled: (featureKey: string) => boolean
    getFeatureLimit: (featureKey: string) => number | null
    shouldShowUpgradePrompt: (featureKey: string) => boolean
}

const BillingContext = createContext<BillingContextType | undefined>(undefined)

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { data: subscription, isLoading } = useSubscription()

    // features is the plan's entitlement document: booleans gate, numbers cap, null is unlimited
    const isFeatureEnabled = (featureKey: string): boolean => {
        const value = subscription?.plan?.features?.[featureKey]
        if (value === true) return true
        if (value === null) return true
        return typeof value === 'number' && value > 0
    }

    const getFeatureLimit = (featureKey: string): number | null => {
        const value = subscription?.plan?.features?.[featureKey]
        return typeof value === 'number' ? value : null
    }

    const shouldShowUpgradePrompt = (featureKey: string): boolean => {
        if (!subscription) return false
        if (isFeatureEnabled(featureKey)) return false
        return (subscription.price ?? 0) === 0
    }

    const value = {
        subscription: subscription ?? null,
        isLoading,
        isFeatureEnabled,
        getFeatureLimit,
        shouldShowUpgradePrompt,
    }

    return (
        <BillingContext.Provider value={value}>
            {children}
        </BillingContext.Provider>
    )
}

export const useBilling = () => {
    const context = useContext(BillingContext)
    if (context === undefined) {
        throw new Error('useBilling must be used within a BillingProvider')
    }
    return context
}
